'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import Sticky from 'react-sticky-el';

import { CreateDonationRequestModeEnum } from '@/app/api';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import TabsSelect, { TabsSelectOption } from '@/app/components/TabsSelect/TabsSelect';

interface DonateBoxProps extends PropsWithClassname {
  donateLink: string;
  selectedDonationMode: CreateDonationRequestModeEnum;
  onChangeSelectedDonationMode: (nextDonationMode: CreateDonationRequestModeEnum) => void;
  donationModeOptions: TabsSelectOption<CreateDonationRequestModeEnum>[];
  isProjectLongTerm: boolean;
  onShareButtonClick: () => void;
  onDonateClick: () => void;
}

const DonateBoxCommercial = ({
  donateLink,
  donationModeOptions,
  selectedDonationMode,
  isProjectLongTerm,
  onChangeSelectedDonationMode,
  onShareButtonClick,
  className,
  onDonateClick,
}: DonateBoxProps) => {
  const { t } = useTranslation();

  const stickyContent = useMemo(
    () => (
      <div className={classNames('flex lg:flex-col py-[20px] lg:py-[12px]')}>
        <Button
          style={ButtonStyle.PRIMARY}
          className="grow text-center mr-[16px] lg:mr-0 lg:mb-[16px] lg:px-[24px] lg:py-[20px]"
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onDonateClick }}
        >
          {t('components.DonateBox.donateCommercial')}
        </Button>
        <Button
          style={ButtonStyle.PRIMARY_OUTLINED}
          className="grow-0 lg:px-[24px] lg:py-[20px]"
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onShareButtonClick }}
        >
          {t('components.DonateBox.share')}
        </Button>
      </div>
    ),
    [donateLink, onShareButtonClick, t],
  );

  return (
    <div className={classNames(className, '')}>
      {isProjectLongTerm && (
        <TabsSelect<CreateDonationRequestModeEnum>
          value={selectedDonationMode}
          onChange={onChangeSelectedDonationMode}
          options={donationModeOptions}
          className="my-[16px]"
        />
      )}
      <div className="block lg:hidden">
        <Sticky stickyClassName="!w-full left-0 px-[24px] shadow-lg bg-white z-20">{stickyContent}</Sticky>
      </div>
      <div className="hidden lg:block">{stickyContent}</div>
    </div>
  );
};

export default DonateBoxCommercial;
